import type { TranslationKeys } from "../../i18n/types";
import { FLAT_RATE_WAGE_MAX } from "../DepartmentWage.config";
import { timeWindowError } from "../floatHours";
import type { BreakRulesConfig, HoursRateConfig, NurseRole, OvertimeConfig } from "../models";
import { OvertimeConfigType, RateHoursBonusType } from "../models";

/*
 * Save-blocking reasons for the three wage rule tables, as translation keys. Owned by the Team
 * page's Wage Settings tab since DAT-905: each section disables its own Save with these, so a
 * problem is always reported next to the table that has it.
 */

/*
 * The server's bounds (DAT-905), mirrored so Save is disabled with a reason before anything is sent,
 * instead of the user clicking it and getting a 400. Same keys the backend raises.
 */
const HOURS_PER_DAY = 24;
const HOURS_PER_WEEK = 168;
const MINUTES_PER_DAY = 1440;

/**
 * The hours-rate name is copied into wage breakdown rows with a suffix appended, and that column is
 * VARCHAR(50). The backend derives the max as `50 - len(" Consecutive OT")`, its longest suffix.
 */
export const HOURS_RATE_NAME_MAX_LENGTH = 35;

/** `min ≤ value ≤ max`, and a real number: NaN and ±Infinity are always out of range. */
const isWithin = (value: number, min: number, max: number): boolean => Number.isFinite(value) && value >= min && value <= max;

/** Daily and consecutive-day limits are hours in one day; weekly and "all" are hours in a week. */
const overtimeHoursLimitMax = (type: OvertimeConfigType): number =>
	type === OvertimeConfigType.daily || type === OvertimeConfigType.consecutive_day ? HOURS_PER_DAY : HOURS_PER_WEEK;

/** A rule name is required, and unique within its table once trimmed (the backend compares trimmed names). */
const nameProblems = (names: (string | null | undefined)[]): { missing: boolean; duplicate: boolean } => {
	// A null name (older rows) is reported as missing, not defaulted: it must be entered before saving.
	const trimmed = names.map((name) => (typeof name === "string" ? name.trim() : ""));
	const entered = trimmed.filter((name) => name !== "");
	return { missing: entered.length < trimmed.length, duplicate: new Set(entered).size < entered.length };
};

export function getHoursRateConfigReasons(configs: HoursRateConfig[] | undefined): TranslationKeys[] {
	const reasons: TranslationKeys[] = [];
	if (!configs || configs.length === 0) return reasons;

	let needsRoles = false;
	let needsBonusType = false;
	let hasInvalidTimeRange = false;
	let bonusOutOfRange = false;
	let nameTooLong = false;
	let timeRangeIncomplete = false;

	for (const config of configs) {
		if (!isWithin(config.bonusVal, 0, FLAT_RATE_WAGE_MAX)) bonusOutOfRange = true;
		// An hours-rate name is optional: the create form seeds a nameless no-op row (bonus 0, all day)
		// that 104 departments still carry, and requiring a name would lock their Save until they named
		// it. So a blank or null name is accepted as-is, and only a name that is there is length-checked
		// (untrimmed, since the name is stored as sent).
		if (typeof config.name === "string" && config.name.length > HOURS_RATE_NAME_MAX_LENGTH) nameTooLong = true;
		// Both ends, or neither (all day). One alone is refused by the backend rather than guessed at.
		if ((config.startTime == null || config.startTime === "") !== (config.endTime == null || config.endTime === "")) timeRangeIncomplete = true;
		if (config.roles == null) needsRoles = true;
		if (config.bonusType == null || (config.bonusType !== RateHoursBonusType.FlatRate && config.bonusType !== RateHoursBonusType.Percentage)) {
			needsBonusType = true;
		}

		const rawInput = (config as HoursRateConfig & { _rawInputValue?: string })._rawInputValue;
		const valueToValidate =
			rawInput !== undefined
				? rawInput
				: config.startTime && config.endTime
					? `${config.startTime.split(":").slice(0, 2).join(":")}-${config.endTime.split(":").slice(0, 2).join(":")}`
					: "";
		// Empty = "all day" (valid)
		if (valueToValidate && timeWindowError(valueToValidate) !== null) hasInvalidTimeRange = true;
	}

	if (needsRoles) reasons.push("department.config.rates.hoursRate.rolesRequired");
	if (needsBonusType) reasons.push("department.config.rates.hoursRate.bonusTypeRequired");
	if (hasInvalidTimeRange) reasons.push("department.config.rates.hoursRate.invalidTimeRange");
	if (timeRangeIncomplete) reasons.push("department.config.rates.hoursRate.timeRangeIncomplete");
	if (bonusOutOfRange) reasons.push("department.config.rates.hoursRate.bonusOutOfRange");
	if (nameTooLong) reasons.push("department.config.rates.hoursRate.nameTooLong");

	return reasons;
}

/**
 * A consecutive-day bound is a whole number of days, 1 or more.
 *
 * The inputs are `type="number"`, so `1.5` and `Infinity` are typeable and reach state — and the
 * backend field is a Pydantic `int`, which 422s on both. Checking `>= 1` alone let those through and
 * turned a form-level message into an unexplained API failure.
 */
const isWholeDayCount = (value: number | null | undefined): value is number => value != null && Number.isInteger(value) && value >= 1;

/** Below the minimum, e.g. 0 or -1 — reported as "enter a day of 1 or more", not as a rounding problem. */
const isBelowOne = (value: number | null | undefined): boolean => value != null && Number.isFinite(value) && value < 1;

/** Entered, at least 1, but not a whole number — 1.5, 8.5, NaN, Infinity. */
const isPresentAndNotWhole = (value: number | null | undefined): boolean => value != null && !isBelowOne(value) && !isWholeDayCount(value);

/**
 * Mirrors the backend `_validate_overtime_tier_monotonicity`, so a non-monotonic consecutive ladder
 * disables Save with a translated message instead of failing as a 400 after the click.
 *
 * Grouped by everything that decides WHICH nurses a rule matches, plus `rateType` — `rate` means
 * `base × rate/100` for a percentage and `base + rate` for a flat rate, so only like-for-like tiers
 * are comparable without a wage in hand. Consecutive rules only: daily/weekly ladders are a
 * pre-existing hazard this feature does not own.
 */
function hasDescendingConsecutiveTier(configs: OvertimeConfig[]): boolean {
	const ladders = new Map<string, OvertimeConfig[]>();
	for (const config of configs) {
		if (config.type !== OvertimeConfigType.consecutive_day) continue;
		const key = JSON.stringify([
			[...(config.roles ?? [])].sort(),
			[...(config.jobIds ?? [])].sort(),
			[...(config.employmentType ?? [])].sort(),
			[...(config.union ?? [])].sort(),
			config.isUnion ?? null,
			config.consecutiveWindow ?? null,
			config.consecutiveDaysFrom ?? null,
			config.consecutiveDaysTo ?? null,
			config.rateType ?? null,
		]);
		const ladder = ladders.get(key);
		if (ladder) ladder.push(config);
		else ladders.set(key, [config]);
	}

	for (const ladder of ladders.values()) {
		const ordered = [...ladder].sort((a, b) => (a.hoursLimit ?? 0) - (b.hoursLimit ?? 0) || (a.rate ?? 0) - (b.rate ?? 0));
		for (let i = 1; i < ordered.length; i++) {
			const lower = ordered[i - 1];
			const higher = ordered[i];
			if ((higher.hoursLimit ?? 0) > (lower.hoursLimit ?? 0) && (higher.rate ?? 0) < (lower.rate ?? 0)) return true;
		}
	}
	return false;
}

export function getOvertimeConfigReasons(configs: OvertimeConfig[] | undefined, deptRoles: NurseRole[]): TranslationKeys[] {
	const reasons: TranslationKeys[] = [];
	if (!configs || configs.length === 0) return reasons;

	let needsRoles = false;
	let needsEmploymentTypes = false;
	let needsConsecutiveDays = false;
	let hasInvalidConsecutiveRange = false;
	let hasNonWholeConsecutiveDays = false;
	let needsConsecutiveWindow = false;
	let hoursLimitOutOfRange = false;
	let rateOutOfRange = false;

	for (const config of configs) {
		if (!isWithin(config.hoursLimit, 0, overtimeHoursLimitMax(config.type))) hoursLimitOutOfRange = true;
		if (!isWithin(config.rate, 0, FLAT_RATE_WAGE_MAX)) rateOutOfRange = true;
		if (deptRoles.length > 0 && (config.roles == null || config.roles.length === 0)) needsRoles = true;
		const empTypes = Array.isArray(config.employmentType) ? config.employmentType : [];
		if (empTypes.length === 0) needsEmploymentTypes = true;
		// Mirrors the backend validator: a consecutive rule with no start day would silently mean
		// "every worked day", so it has to be entered rather than defaulted.
		if (config.type === OvertimeConfigType.consecutive_day) {
			// The window is seeded when the type is switched in the table, but a row loaded from the
			// database can still carry NULL. Without this check the form allows a save the backend
			// rejects, and the user gets a bare 400 with nothing pointing at the field.
			if (config.consecutiveWindow == null) needsConsecutiveWindow = true;

			const from = config.consecutiveDaysFrom;
			const to = config.consecutiveDaysTo;
			// Each failure gets its OWN message. Folding "not a whole number" into the range error told
			// a user who typed `to = 8.5` that 8.5 was "before" 7 — both wrong and unactionable.
			if (from == null || isBelowOne(from)) needsConsecutiveDays = true;
			if (isPresentAndNotWhole(from) || isPresentAndNotWhole(to)) hasNonWholeConsecutiveDays = true;
			if (isWholeDayCount(from) && isWholeDayCount(to) && to < from) hasInvalidConsecutiveRange = true;
		}
	}

	const overtimeNames = nameProblems(configs.map((config) => config.name));
	if (overtimeNames.missing) reasons.push("department.config.rates.overtime.nameRequired");
	if (overtimeNames.duplicate) reasons.push("department.config.rates.overtime.nameDuplicate");
	if (needsRoles) reasons.push("department.config.rates.overtime.rolesRequired");
	if (needsEmploymentTypes) reasons.push("department.config.rates.overtime.employmentTypesRequired");
	if (needsConsecutiveWindow) reasons.push("department.config.rates.overtime.consecutiveWindowRequired");
	if (needsConsecutiveDays) reasons.push("department.config.rates.overtime.consecutiveDaysRequired");
	if (hasNonWholeConsecutiveDays) reasons.push("department.config.rates.overtime.consecutiveDaysWhole");
	if (hasInvalidConsecutiveRange) reasons.push("department.config.rates.overtime.consecutiveRangeInvalid");
	if (hasDescendingConsecutiveTier(configs)) reasons.push("department.config.rates.overtime.consecutiveTierRateDrop");
	if (hoursLimitOutOfRange) reasons.push("department.config.rates.overtime.hoursLimitOutOfRange");
	if (rateOutOfRange) reasons.push("department.config.rates.overtime.rateOutOfRange");

	return reasons;
}

export function getBreakRulesConfigReasons(configs: BreakRulesConfig[] | undefined, deptRoles: NurseRole[]): TranslationKeys[] {
	const reasons: TranslationKeys[] = [];
	if (!configs || configs.length === 0) return reasons;

	let needsRoles = false;
	let needsEmploymentTypes = false;
	let breakLimitOutOfRange = false;
	let breakStartOutOfRange = false;
	let breakTimeOutOfRange = false;
	let breakTimeNotWholeMinutes = false;

	for (const config of configs) {
		if (deptRoles.length > 0 && (config.roles == null || config.roles.length === 0)) needsRoles = true;
		const empTypes = Array.isArray(config.employmentTypes) ? config.employmentTypes : [];
		if (empTypes.length === 0) needsEmploymentTypes = true;
		if (!isWithin(config.breakLimit, 0, HOURS_PER_DAY)) breakLimitOutOfRange = true;
		// Optional: unset means "at the break limit", so only an entered value is checked.
		if (config.breakStart != null && !isWithin(config.breakStart, 0, HOURS_PER_DAY)) breakStartOutOfRange = true;
		if (!isWithin(config.breakTime, 0, MINUTES_PER_DAY)) breakTimeOutOfRange = true;
		// `break_time` is an integer column and API field, so 30.5 would pass the range check here and then
		// fail the request with an untranslated 422 — refuse it before Save instead.
		else if (!Number.isInteger(config.breakTime)) breakTimeNotWholeMinutes = true;
	}

	const breakRuleNames = nameProblems(configs.map((config) => config.name));
	if (breakRuleNames.missing) reasons.push("department.config.rates.breakRules.nameRequired");
	if (breakRuleNames.duplicate) reasons.push("department.config.rates.breakRules.nameDuplicate");
	if (needsRoles) reasons.push("department.config.rates.breakRules.rolesRequired");
	if (needsEmploymentTypes) reasons.push("department.config.rates.breakRules.employmentTypesRequired");
	if (breakLimitOutOfRange) reasons.push("department.config.rates.breakRules.breakLimitOutOfRange");
	if (breakStartOutOfRange) reasons.push("department.config.rates.breakRules.breakStartOutOfRange");
	if (breakTimeOutOfRange) reasons.push("department.config.rates.breakRules.breakTimeOutOfRange");
	if (breakTimeNotWholeMinutes) reasons.push("department.config.rates.breakRules.breakTimeWholeMinutes");

	return reasons;
}
